import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@ndrc/ui';
import { formatParisTime } from '../lib/clock';
import { notificationsQueryKey, useNotifications } from '../lib/queries';
import type { NotificationItem } from '../lib/queries';
import { mutateCore } from '../lib/rpc';
import { t } from '../messages';

/**
 * **La cloche : ce que l'élève n'avait pas.**
 *
 * Deux opérations du cœur savaient LIRE des notifications depuis le chantier 5
 * (`notifications.list`, `notifications.markRead`) — personne ne pouvait en créer, et l'élève
 * n'avait de toute façon aucun endroit où les voir. Le cahier des charges (§39) les demande
 * pourtant nommément : « Nouvelle mission », « Nouveau client », « Votre manager vous demande une
 * action ». Ce composant est la moitié « voir » ; `teacher/NotifyPanel.tsx` est la moitié
 * « écrire ».
 *
 * **Sobre, et ça se décide ici.** Une cloche, un compteur, une liste courte, et l'accusé de
 * lecture. Ce n'est PAS une messagerie : l'Inbox est une application à part, prévue plus tard, et
 * tout ce qu'on ajouterait ici (répondre, archiver, filtrer) serait à défaire le jour où elle
 * arrivera. Vingt lignes au plus (`useNotifications`), pas de pagination, pas de recherche.
 *
 * **Elle se met à jour toute seule.** Aucun sondage propre à cet écran : le fil temps réel invalide
 * `notifications.list` quand un signal `core.notifications.changed` arrive
 * (`lib/useRealtimeSignals.ts`), et TanStack Query relit. Le délai est celui du battement du fil —
 * cinq secondes onglet visible, dix onglet caché.
 *
 * **Elle est là pour tout le monde**, élève comme enseignant : une notification est une donnée
 * personnelle, et le cœur ne rend jamais que celles de la session. Rien ici ne teste le rôle.
 */
/**
 * L'heure d'arrivée, en heure de Paris. `Date.parse` sur la chaîne rendue par Postgres (elle porte
 * son décalage), jamais `new Date()` — `lib/clock.ts` reste le seul du bureau à fabriquer un
 * instant (`apps/desktop/CLAUDE.md`). Une chaîne illisible rend une chaîne vide plutôt qu'un
 * « Invalid Date » sous les yeux d'un élève.
 */
function whenOf(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : formatParisTime(new Date(ms));
}

export function NotificationsBell(): JSX.Element {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Un clic ailleurs, ou Échap, referme — le comportement attendu de n'importe quel menu de cette
  // barre (voir `MenuBar`, dont ce panneau reprend la matière et le geste).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (panelRef.current?.contains(event.target as Node) === false) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const items: readonly NotificationItem[] = notifications.data?.notifications ?? [];
  const unread = items.filter((item) => item.readAt === null);

  /**
   * L'accusé de lecture. Le cœur refuse (`NOT_FOUND`) une notification qui n'est pas celle de
   * l'appelant, ou déjà lue — on relit la liste dans les deux cas plutôt que d'afficher une erreur
   * pour un geste qui n'a, du point de vue de l'élève, aucune conséquence.
   */
  async function markRead(id: string): Promise<void> {
    setBusy(id);
    try {
      await mutateCore('notifications.markRead', { id });
    } catch {
      // Voir ci-dessus : rien à dire à l'élève, la relecture ci-dessous rétablit la vérité.
    } finally {
      setBusy(null);
      await queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    }
  }

  return (
    <div className="menubar-bell-wrap" ref={panelRef}>
      <button
        type="button"
        className="menubar-bell"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          unread.length === 0 ? t('bell.label') : t('bell.labelUnread', { count: unread.length })
        }
        title={
          unread.length === 0 ? t('bell.label') : t('bell.labelUnread', { count: unread.length })
        }
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Icon name="bell" size={14} strokeWidth={1.7} />
        {unread.length === 0 ? null : (
          <span className="menubar-bell-count">{String(unread.length)}</span>
        )}
      </button>
      {open ? (
        <div className="bell-menu" role="menu">
          <p className="bell-menu-title">{t('bell.title')}</p>
          {items.length === 0 ? (
            <p className="bell-empty">{t('bell.empty')}</p>
          ) : (
            <ul className="bell-list">
              {items.map((item) => (
                <li
                  key={item.id}
                  className={item.readAt === null ? 'bell-item bell-item--unread' : 'bell-item'}
                >
                  <p className="bell-item-title">
                    {item.level === 'important' ? (
                      <span className="bell-item-flag">{t('bell.important')}</span>
                    ) : null}
                    {item.title}
                  </p>
                  {item.body === null ? null : <p className="bell-item-body">{item.body}</p>}
                  <p className="bell-item-foot">
                    <span className="bell-item-when">{whenOf(item.createdAt)}</span>
                    {item.readAt === null ? (
                      <button
                        type="button"
                        className="bell-item-read"
                        disabled={busy === item.id}
                        onClick={() => {
                          void markRead(item.id);
                        }}
                      >
                        {t('bell.markRead')}
                      </button>
                    ) : (
                      <span className="bell-item-done">{t('bell.read')}</span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
